import { inject, Injectable, signal } from '@angular/core';
import { TaskQueueInterface, TaskQueueStorageServiceInterface } from './interfaces';
import { Activity, ActivityAssociation, CRMActivityApiService, UpdateActivityRequestInterface } from '@vendasta/crm';
import { firstValueFrom, map, of } from 'rxjs';
import {
  CrmInjectionToken,
  CrmMultilocationInjectionToken,
  MultiLocationContext,
  PAGE_ROUTES,
} from '@galaxy/crm/static';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Router } from '@angular/router';

export interface Action {
  type: 'complete' | 'reschedule';
  undoRequest?: UpdateActivityRequestInterface;
}

interface AssociationWithNamespace extends ActivityAssociation {
  namespace?: string;
}

@Injectable()
export class TaskQueueService {
  private readonly storageService = inject(TaskQueueStorageServiceInterface);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly config = inject(CrmInjectionToken);
  private readonly activityApiService = inject(CRMActivityApiService);
  private readonly router = inject(Router);
  private readonly multiLocationConfig = inject(CrmMultilocationInjectionToken);

  loading = signal(false);
  taskQueue = signal<TaskQueueInterface | null>(null);
  action = signal<Action | null>(null);
  private currentTaskNamespace: string | null = null;
  private taskNamespaces = new Map<string, string>();
  private objectNamespaces = new Map<string, string>();

  public setTaskNamespaces(namespaces: Map<string, string>): void {
    this.taskNamespaces = new Map(namespaces);
  }

  public getTaskNamespace(taskId: string): string | undefined {
    return this.taskNamespaces.get(taskId);
  }

  public hasTaskNamespaces(): boolean {
    return this.taskNamespaces.size > 0;
  }

  public async startQueue(taskIds: string[], namespace?: string, startIndex = 0): Promise<void> {
    try {
      this.loading.set(true);
      const isMultiLocation = await firstValueFrom(this.multiLocationConfig.isMultiLocation$ ?? of(false));

      let queue: TaskQueueInterface;

      if (isMultiLocation) {
        const tasksWithNamespaces = this.hasTaskNamespaces()
          ? taskIds.filter((id) => this.getTaskNamespace(id))
          : taskIds;

        if (tasksWithNamespaces.length === 0) {
          this.loading.set(false);
          return;
        }

        queue = {
          tasks: tasksWithNamespaces.map((id) => ({
            id,
            namespace: this.getTaskNamespace(id) || '',
          })),
          current: startIndex,
        };
      } else {
        queue = {
          tasks: taskIds.map((id) => ({
            id,
            namespace: namespace || '',
          })),
          current: startIndex,
        };
      }

      await this.saveQueue(queue);
      this.navigateToCurrentTask(queue);
      this.analyticsService.trackEvent('task-queue-start-queue', 'sales-tasks', 'click', 1);
    } finally {
      this.loading.set(false);
    }
  }

  public queueCurrentID(queue: TaskQueueInterface | null): string | null {
    if (queue) {
      return queue.tasks[queue.current]?.id || null;
    }
    return null;
  }

  public async fetchCurrentTask(id: string | null): Promise<Activity | null> {
    if (!id) {
      return null;
    }

    try {
      this.loading.set(true);
      const queue = await this.storageService.load();
      const currentTask = queue?.tasks[queue.current];

      const isMultiLocation = await firstValueFrom(this.multiLocationConfig.isMultiLocation$ ?? of(false));

      if (isMultiLocation) {
        if (this.hasTaskNamespaces() && this.getTaskNamespace(id)) {
          const namespace = this.getTaskNamespace(id)!;

          try {
            const result = await this.fetchTaskWithNamespace(id, namespace);
            if (result) {
              return result;
            }
          } catch (error) {
            return null;
          }
        }

        return null;
      } else {
        let taskNamespace: string | undefined;

        if (currentTask?.namespace) {
          taskNamespace = currentTask.namespace;
        } else if (this.currentTaskNamespace) {
          taskNamespace = this.currentTaskNamespace;
        }

        if (!taskNamespace) {
          return null;
        }

        try {
          const result = await this.fetchTaskWithNamespace(id, taskNamespace);
          return result;
        } catch (error) {
          return null;
        }
      }
    } catch (error) {
      return null;
    } finally {
      this.currentTaskNamespace = null;
      this.loading.set(false);
    }
  }

  private async fetchTaskWithNamespace(id: string, namespace: string): Promise<Activity | null> {
    return await firstValueFrom(
      this.activityApiService.getMultiActivity({ namespace, crmObjectIds: [id] }).pipe(
        map((result) => {
          const activity = result?.activities[0];
          if (activity?.associations) {
            activity.associations.forEach((association) => {
              if (association.crmObjectType === 'Contact') {
                this.objectNamespaces.set(`Contact:${association.crmObjectId}`, namespace);
              }
            });
          }
          return activity;
        }),
        map((activity) => (activity?.crmObjectId ? activity : null)),
      ),
    );
  }

  private getAssociationRoute(association: ActivityAssociation): string | null {
    switch (association.crmObjectType) {
      case 'Contact':
        return `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
      case 'Company':
        return `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
      default:
        return null;
    }
  }

  public async saveQueue(queue: TaskQueueInterface): Promise<void> {
    await this.storageService.save(queue);
    this.taskQueue.set({ ...queue });
  }

  public async navigateToCurrentTask(queue: TaskQueueInterface): Promise<void> {
    const id = this.queueCurrentID(queue);
    const task = await this.fetchCurrentTask(id);
    this.action.set(null);
    await this.navigateToPrimaryAssociation(task);
  }

  private async navigateToPrimaryAssociation(task: Activity | null): Promise<void> {
    if (task) {
      const contactAssociation = task.associations?.find((a) => a.crmObjectType === 'Contact');
      const companyAssociation = task.associations?.find((a) => a.crmObjectType === 'Company');
      await this.navigateToAssociation(contactAssociation ?? companyAssociation);
    }
  }

  public async navigateToAssociation(association?: ActivityAssociation): Promise<void> {
    if (!association) return;

    const isMultiLocation = await this.isMultiLocationEnabled();

    if (isMultiLocation && this.isContactAssociation(association)) {
      await this.handleMultiLocationNavigation(association);
      return;
    }

    await this.handleStandardNavigation(association);
  }

  private async handleMultiLocationNavigation(association: AssociationWithNamespace): Promise<void> {
    const namespace =
      this.objectNamespaces.get(`Contact:${association.crmObjectId}`) || (await this.determineNamespace());

    const multiLocationContext = await this.getMultiLocationContext();
    const brandId = multiLocationContext?.groupId;
    const crmObjectId = association.crmObjectId;

    const routePrefix = await firstValueFrom(this.multiLocationConfig.routePrefix$ ?? of(`/account/brands/${brandId}`));

    const url = this.buildMultiLocationUrl(routePrefix, namespace, crmObjectId);
    await this.router.navigateByUrl(url);
  }

  private async handleStandardNavigation(association: ActivityAssociation): Promise<void> {
    const associationRoute = this.getAssociationRoute(association);
    if (!associationRoute) return;

    const routePrefix = await firstValueFrom(this.config.routePrefix$);

    this.analyticsService.trackEvent('task-queue-view-association', 'sales-tasks', 'click', 1, {
      type: routePrefix,
    });

    const url = this.buildStandardUrl(routePrefix, associationRoute, association.crmObjectId);
    await this.router.navigateByUrl(url);
  }

  private async isMultiLocationEnabled(): Promise<boolean> {
    return firstValueFrom(this.multiLocationConfig.isMultiLocation$ ?? of(false));
  }

  private isContactAssociation(association: ActivityAssociation): boolean {
    return association.crmObjectType === 'Contact';
  }

  private async determineNamespace(): Promise<string> {
    return (
      this.currentTaskNamespace ||
      (await this.getNamespaceFromMultilocationContext()) ||
      (await firstValueFrom(this.config.namespace$ ?? of('')))
    );
  }

  private async getMultiLocationContext(): Promise<MultiLocationContext | null> {
    return firstValueFrom(this.multiLocationConfig.multiLocationContext$ ?? of(null));
  }

  private buildMultiLocationUrl(routePrefix: string, namespace: string, crmObjectId: string): string {
    const viewRoute = `contacts/${namespace}/profile/${crmObjectId}`;
    return `${routePrefix}/${viewRoute}`;
  }

  private buildStandardUrl(routePrefix: string, route: string, objectId: string): string {
    const associationUrl = route.replace(':crmObjectId', objectId);
    return `${routePrefix}/${associationUrl}`;
  }

  private async getNamespaceFromMultilocationContext(): Promise<string | null> {
    try {
      const context = await this.getMultiLocationContext();
      return context?.accountGroupsIds?.[0] || null;
    } catch {
      return null;
    }
  }

  public setCurrentTaskNamespace(namespace: string): void {
    if (namespace) {
      this.currentTaskNamespace = namespace;
    }
  }
}
